import axios from 'axios';
import { baseAPI } from '../config';

/**
 * Get a signed URL from an S3 key
 * @param {string} s3Key - S3 key (path) of the object
 * @returns {Promise<string>} Signed URL
 */
export const getSignedUrl = async s3Key => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${baseAPI}/documents/get-signed-url`,
      { s3Key },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data?.url || null;
  } catch (error) {
    console.error('Error getting signed URL:', error);
    throw error;
  }
};

/**
 * Get a presigned URL for uploading a file to S3
 * @param {string} expedientId - Expedient ID
 * @param {string} slug - File slug (path within expedient folder)
 * @param {string} fileName - Original file name
 * @param {string} fileType - MIME type of the file
 * @returns {Promise<string>} Presigned URL for upload
 */
export const getPresignedUploadUrl = async (expedientId, slug, fileName, fileType) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${baseAPI}/documents/signed-url`,
      {
        expedient_id: expedientId,
        slug,
        file_name: fileName,
        file_type: fileType,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data?.url || null;
  } catch (error) {
    console.error('Error getting presigned upload URL:', error);
    throw error;
  }
};

/**
 * Get a download URL for a document from S3
 * @param {string} expedientId - Expedient ID
 * @param {string} slug - File slug (path within expedient folder)
 * @returns {Promise<string>} Signed URL for download
 */
export const getDownloadUrl = async (expedientId, slug) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${baseAPI}/documents/download`,
      {
        slug,
        expedient_id: expedientId,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data?.url || null;
  } catch (error) {
    console.error('Error getting download URL:', error);
    throw error;
  }
};

/**
 * Upload a file to S3 using a presigned URL
 * @param {File} file - File to upload
 * @param {string} expedientId - Expedient ID
 * @param {string} slug - File slug (path within expedient folder)
 * @param {string} fileName - Original file name
 * @param {string} fileType - MIME type of the file
 * @returns {Promise<{s3Key: string, url: string}>} S3 key and the presigned URL used
 */
export const uploadFileToS3 = async (file, expedientId, slug, fileName, fileType) => {
  try {
    // Get presigned URL
    const presignedUrl = await getPresignedUploadUrl(expedientId, slug, fileName, fileType);

    // Upload file directly to S3 using presigned URL
    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': fileType,
      },
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed with status: ${uploadResponse.status}`);
    }

    // Return the S3 key for reference
    const s3Key = `${expedientId}/${slug}`;
    return {
      s3Key,
      url: presignedUrl,
    };
  } catch (error) {
    console.error('Error uploading file to S3:', error);
    throw error;
  }
};

/**
 * Clasifica un documento del expediente: que es, y si se le puede enviar al
 * deudor.
 *
 * Solo tiene sentido en expedientes AR; el backend lo exige y devuelve 400 en
 * los demas.
 */
export const classifyDocument = async (expedientId, documentId, patch) => {
  const token = localStorage.getItem('token');
  const { data } = await axios.patch(
    `${baseAPI}/documents/${expedientId}/${documentId}/classification`,
    patch,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
};

/**
 * Los documentos que ya tiene el expediente, para adjuntar uno a un correo sin
 * volver a subirlo.
 *
 * Cada fila trae la **clave de S3 ya montada** por el servidor. No trae el campo
 * `url` del documento a propósito: es una URL firmada y caduca, y lo que hace
 * falta para adjuntar es la clave.
 */
export const listExpedientDocuments = async expedientId => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(`${baseAPI}/documents/${expedientId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data?.documents || [];
};
